'use client';

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Typography, ButtonBase, Grid, Card, CardContent } from '@mui/material';
import { Settings, CreditCard, Users } from 'lucide-react';
import { isAdminOrOwner } from '../utils/hasPermission';

const settings = [
  {
    id: 1,
    title: 'System and Service Setting',
    description: 'Manage application-wide configurations and services.',
    icon: <Settings />,
    link: '/dashboard/settings/app',
    adminOrOwnerOnly: true,
  },
  {
    id: 2,
    title: 'Payment Settings',
    description: 'Configure payment gateways, billing, and subscription plans.',
    icon: <CreditCard />,
    link: '/dashboard/settings/payment',
    adminOrOwnerOnly: true,
  },
  {
    id: 3,
    title: 'Customer Care Settings',
    description: 'Set up communication channels for customer support.',
    icon: <Users />,
    link: '/dashboard/settings/customers',
  },
];

export default function AppSetting() {
  const navigate = useNavigate();
  const { user, activeApp } = useSelector((state) => state.auth);
  const isPrivileged = isAdminOrOwner(user, activeApp);

  const visibleSettings = settings.filter((data) => {
    if (data.adminOrOwnerOnly && !isPrivileged) return false;
    return true;
  });

  const handleNavigation = (route) => {
    navigate(route);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', p: 4 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h4" fontWeight={700} color="text.primary" sx={{ mb: 1 }}>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          You can customize how the system should behave here.
        </Typography>

        <Grid container spacing={3}>
          {visibleSettings.map((data) => (
            <Grid item xs={12} sm={6} md={4} key={data.id}>
              <Card
                component={ButtonBase}
                onClick={() => handleNavigation(data.link)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  p: 3,
                  borderRadius: 3,
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  textAlign: 'left',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'grey.100',
                    borderColor: 'primary.main',
                    boxShadow: '0 6px 12px -2px rgb(0 0 0 / 0.15), 0 3px 6px -2px rgb(0 0 0 / 0.08)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>{React.cloneElement(data.icon, { size: 36 })}</Box>
                  <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
                    {data.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {data.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
